import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Input,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Option,
  TagGroup,
  Text,
  Textarea,
  ToggleButton,
} from '@fluentui/react-components';
import type { InputOnChangeData } from '@fluentui/react-components';
import {
  Add16Regular,
  Add20Regular,
  Checkmark16Regular,
  Delete16Regular,
  Dismiss16Regular,
} from '@fluentui/react-icons';
import { useMemo, useState } from 'react';

import {
  ENGAGEMENT_TYPES,
  INTERACTION_TYPES,
  REASONING_INPUT_KEY,
  SCENARIO_LIBRARY,
} from './expectedAction.constants.js';
import type {
  EngagementTypeDef,
  InteractionType,
} from './expectedAction.interface.js';
import {
  addCustomizationAsk,
  addScenario,
  createEmptyDraft,
  getEngagementInput,
  hasEngagementType,
  removeCustomizationAsk,
  removeScenario,
  setEngagementInput,
  toggleEngagementType,
  toggleInteractionType,
} from './expectedActionDraft.js';
import type { ExpectedActionFormProps } from './ExpectedActionForm.interface.js';
import { useStyles } from './ExpectedActionForm.style.js';

const REASONING_FIELD_LABEL = 'Reasoning';
const REASONING_PLACEHOLDER = 'Why did you choose this action?';
const CUSTOM_SCENARIO_PLACEHOLDER = 'Custom scenario...';
const ADD_SCENARIO_PLACEHOLDER = 'Add scenario...';
const ADD_CUSTOMIZATION_PLACEHOLDER = 'Add customization ask...';

function scenarioName(
  scenarios: readonly { id: string; name: string }[],
  id: string,
): string {
  return scenarios.find((scenario) => scenario.id === id)?.name ?? id;
}

export function ExpectedActionForm({
  disabled = false,
  initialValue,
  onDone,
  onCancel,
  onDelete,
  engagementTypes = ENGAGEMENT_TYPES,
  scenarios = SCENARIO_LIBRARY,
}: ExpectedActionFormProps) {
  const styles = useStyles();
  const [draft, setDraft] = useState(initialValue ?? createEmptyDraft());
  const [customScenario, setCustomScenario] = useState('');
  const [customizationAsk, setCustomizationAsk] = useState('');

  const availableScenarios = useMemo(
    () =>
      scenarios.filter(
        (scenario) => !draft.associatedScenarios.includes(scenario.id),
      ),
    [scenarios, draft.associatedScenarios],
  );

  const handleAddCustomScenario = () => {
    setDraft((current) => addScenario(current, customScenario));
    setCustomScenario('');
  };

  const handleAddCustomizationAsk = () => {
    setDraft((current) => addCustomizationAsk(current, customizationAsk));
    setCustomizationAsk('');
  };

  const renderEngagementInputs = (engagementType: EngagementTypeDef) => (
    <div className={styles.engagementInputs}>
      {engagementType.inputs.map((input) => {
        const value = getEngagementInput(draft, engagementType.id, input.name);
        const onChange = (next: string) =>
          setDraft((current) =>
            setEngagementInput(current, engagementType.id, input.name, next),
          );
        return (
          <div key={input.name}>
            <Text size={200} className={styles.fieldLabel}>
              {input.name}
            </Text>
            {input.type === 'enum' ? (
              <Dropdown
                aria-label={`${engagementType.name} ${input.name}`}
                placeholder="Select..."
                value={value}
                selectedOptions={value === '' ? [] : [value]}
                onOptionSelect={(_event, data) =>
                  onChange(data.optionValue ?? '')
                }
              >
                {(input.options ?? []).map((optionValue) => (
                  <Option key={optionValue} value={optionValue}>
                    {optionValue}
                  </Option>
                ))}
              </Dropdown>
            ) : (
              <Textarea
                aria-label={`${engagementType.name} ${input.name}`}
                value={value}
                onChange={(_event, data) => onChange(data.value)}
                resize="vertical"
              />
            )}
          </div>
        );
      })}
      <div>
        <Text size={200} className={styles.fieldLabel}>
          {REASONING_FIELD_LABEL}
        </Text>
        <Textarea
          aria-label={`${engagementType.name} reasoning`}
          placeholder={REASONING_PLACEHOLDER}
          value={getEngagementInput(
            draft,
            engagementType.id,
            REASONING_INPUT_KEY,
          )}
          onChange={(_event, data) =>
            setDraft((current) =>
              setEngagementInput(
                current,
                engagementType.id,
                REASONING_INPUT_KEY,
                data.value,
              ),
            )
          }
          resize="vertical"
        />
      </div>
    </div>
  );

  return (
    <section className={styles.root} aria-label="Expected action">
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Add20Regular />
          <Text weight="semibold">Expected action</Text>
        </div>
        {onDelete !== undefined && (
          <div className={styles.headerActions}>
            <Button
              appearance="subtle"
              size="small"
              icon={<Delete16Regular />}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      <div className={styles.body}>
        <div>
          <Text size={200} weight="semibold" className={styles.sectionLabel}>
            Engagement Types
          </Text>
          <div className={styles.engagementList}>
            {engagementTypes.map((engagementType) => {
              const checked = hasEngagementType(draft, engagementType.id);
              return (
                <div key={engagementType.id} className={styles.engagementItem}>
                  <Checkbox
                    checked={checked}
                    label={engagementType.name}
                    onChange={() =>
                      setDraft((current) =>
                        toggleEngagementType(current, engagementType.id),
                      )
                    }
                  />
                  {checked && renderEngagementInputs(engagementType)}
                </div>
              );
            })}
          </div>
        </div>

        <Divider />

        <div className={styles.inlineControls}>
          <Text size={200} weight="semibold">
            Interaction Type (optional):
          </Text>
          <div className={styles.toggleGroup}>
            {INTERACTION_TYPES.map((interactionType: InteractionType) => (
              <ToggleButton
                key={interactionType}
                size="small"
                checked={draft.interactionTypes.includes(interactionType)}
                appearance={
                  draft.interactionTypes.includes(interactionType)
                    ? 'primary'
                    : 'outline'
                }
                onClick={() =>
                  setDraft((current) =>
                    toggleInteractionType(current, interactionType),
                  )
                }
              >
                {interactionType}
              </ToggleButton>
            ))}
          </div>
        </div>

        <div>
          <Text size={200} weight="semibold" className={styles.sectionLabel}>
            Scenarios (optional):
          </Text>
          <div className={styles.tagRow}>
            {draft.associatedScenarios.length > 0 && (
              <TagGroup
                aria-label="Selected scenarios"
                onDismiss={(_event, data) =>
                  setDraft((current) => removeScenario(current, data.value))
                }
              >
                {draft.associatedScenarios.map((scenarioId) => (
                  <InteractionTag key={scenarioId} value={scenarioId}>
                    <InteractionTagPrimary hasSecondaryAction>
                      {scenarioName(scenarios, scenarioId)}
                    </InteractionTagPrimary>
                    <InteractionTagSecondary
                      aria-label={`Remove ${scenarioName(scenarios, scenarioId)}`}
                    />
                  </InteractionTag>
                ))}
              </TagGroup>
            )}
            <Dropdown
              aria-label="Add scenario to expected action"
              placeholder={ADD_SCENARIO_PLACEHOLDER}
              size="small"
              className={styles.scenarioDropdown}
              selectedOptions={[]}
              value=""
              onOptionSelect={(_event, data) => {
                if (data.optionValue === undefined) {
                  return;
                }
                setDraft((current) => addScenario(current, data.optionValue!));
              }}
            >
              {availableScenarios.map((scenario) => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </Option>
              ))}
            </Dropdown>
            <Input
              aria-label="Custom scenario"
              size="small"
              placeholder={CUSTOM_SCENARIO_PLACEHOLDER}
              value={customScenario}
              onChange={(_event, data: InputOnChangeData) =>
                setCustomScenario(data.value)
              }
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<Add16Regular />}
              aria-label="Add custom scenario"
              disabled={customScenario.trim() === ''}
              onClick={handleAddCustomScenario}
            />
          </div>
        </div>

        <div>
          <Text size={200} weight="semibold" className={styles.sectionLabel}>
            Customization asks (optional):
          </Text>
          <div className={styles.tagRow}>
            {draft.associatedCustomizationAsks.length > 0 && (
              <TagGroup
                aria-label="Selected customization asks"
                onDismiss={(_event, data) =>
                  setDraft((current) =>
                    removeCustomizationAsk(current, data.value),
                  )
                }
              >
                {draft.associatedCustomizationAsks.map((ask) => (
                  <InteractionTag key={ask} value={ask}>
                    <InteractionTagPrimary hasSecondaryAction>
                      {ask}
                    </InteractionTagPrimary>
                    <InteractionTagSecondary aria-label={`Remove ${ask}`} />
                  </InteractionTag>
                ))}
              </TagGroup>
            )}
            <Input
              aria-label="Add customization ask"
              size="small"
              placeholder={ADD_CUSTOMIZATION_PLACEHOLDER}
              value={customizationAsk}
              onChange={(_event, data: InputOnChangeData) =>
                setCustomizationAsk(data.value)
              }
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<Add16Regular />}
              aria-label="Add customization ask entry"
              disabled={customizationAsk.trim() === ''}
              onClick={handleAddCustomizationAsk}
            />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          appearance="primary"
          size="small"
          icon={<Checkmark16Regular />}
          onClick={() => onDone(draft)}
          disabled={disabled}
        >
          Done
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<Dismiss16Regular />}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </section>
  );
}
